import { Theme } from "../../../providers/ThemeProvider";

// Lab provider identifiers come in two shapes: a slug string (e.g. "quest") on
// user/custom panels, and a numeric id (e.g. 7) on provider/default panels.
// This is the single source of truth mapping both to one display label.
type LabProviderConfig = { slug: string; id?: number; label: string };

// Numeric ids come from the backend `enLabID` enum (LabCorp = 5, Quest = 7);
// the slug strings match its Display names ("labcorp"/"quest").
export const LAB_PROVIDERS: LabProviderConfig[] = [
  { slug: "quest", id: 7, label: "Quest Diagnostics" },
  { slug: "labcorp", id: 5, label: "LabCorp" },
];

const providerLabelBySlug: Record<string, string> = {};
const providerLabelById: Record<number, string> = {};
const providerIdBySlug: Record<string, number> = {};
LAB_PROVIDERS.forEach((p) => {
  providerLabelBySlug[p.slug] = p.label;
  if (p.id !== undefined) {
    providerLabelById[p.id] = p.label;
    providerIdBySlug[p.slug] = p.id;
  }
});

// Resolves a provider's numeric lab id (enLabID) from a slug ("quest" -> 7), a
// numeric id (7 -> 7), or a numeric string ("7" -> 7). Returns null if unknown.
export const getProviderId = (
  provider: string | number | null | undefined
): number | null => {
  if (provider === null || provider === undefined || provider === "")
    return null;
  if (typeof provider === "number") return provider;
  if (/^\d+$/.test(provider)) return Number(provider);
  return providerIdBySlug[provider] ?? null;
};

// Resolves a provider's display label from a slug ("quest"), a numeric id (7),
// or a numeric string ("7"). Falls back to the raw value when unknown.
export const getProviderLabel = (
  provider: string | number | null | undefined
): string => {
  if (provider === null || provider === undefined || provider === "") return "";
  const asNumber =
    typeof provider === "number"
      ? provider
      : /^\d+$/.test(provider)
      ? Number(provider)
      : null;
  if (asNumber !== null && providerLabelById[asNumber] !== undefined) {
    return providerLabelById[asNumber];
  }
  if (typeof provider === "string") {
    return providerLabelBySlug[provider] ?? provider;
  }
  return String(provider);
};

export enum MetricType {
  LUNG = 1,
  RESPIRATORY_FUNCTION = 2,
  METABOLISM = 3,
  MITOCHONDRIAL_DYSFUNCTION = 4,
  NERVOUS_SYSTEM = 5,
  POSTURE = 6,
  HEART = 7,
  ENDURANCE = 8,
  RECOVERY = 9,
  BRAIN = 10,
  SEXUAL_WELLNESS_MALE = 11,
  SEXUAL_WELLNESS_FEMALE = 12,
  HUNGER_HORMONES = 13,
  THYROID_HORMONES = 14,
  STRESS_HORMONES = 15,
  INFLAMMATION = 16,
  SLEEP = 17,
  GUT = 18,
  LIVER = 19,
  PANCREAS = 20,
  KIDNEYS = 21,
  BONE = 22,
  AUTOIMMUNITY = 23,
  NUTRITIONAL_STATUS = 24,
  ENVIRONMENTAL_TOXINS = 25,
  BODY_COMPOSITION = 26,
}

export const organConnectionsWithMetric = {
  1: [2, 4, 7, 8, 10],
  2: [1, 3, 5, 6, 10],
  3: [4, 13],
  4: [7, 8, 9, 16],
  5: [2, 7, 9],
  6: [2, 5, 9, 10],
  7: [1, 8, 9, 11, 12],
  8: [3],
  9: [2, 5, 8, 15, 16],
  10: [5, 15, 16, 17],
  11: [5, 10, 16, 17],
  12: [5, 10, 16, 17],
  13: [4, 5, 16, 19, 22],
  14: [3, 9, 10, 11, 12, 22],
  15: [2, 5, 7, 9, 18],
  16: [1, 7, 9, 18, 23],
  17: [4, 5, 9, 10, 14],
  18: [4, 5, 10, 13, 16],
  19: [7, 10, 21, 22, 23],
  20: [1, 4, 18, 19, 22],
  21: [7, 10, 16, 18],
  22: [6],
  23: [5, 18, 16, 20, 22],
  24: [3, 4, 13, 14],
  25: [4, 10, 11, 12, 16, 19],
};

export const programmingTabs = [
  { label: "Nutrition", key: 0 },
  { label: "Workout", key: 1 },
  { label: "Bio optimization", key: 2 },
];

export const clientInfoTabs = [
  { label: "Biometrics", key: 0 },
  { label: "Nutrition", key: 1 },
  { label: "Biomarkers", key: 2 },
  { label: "Prescription", key: 3 },
];

export const homePageTabs = [
  { label: "Overview", key: 0 },
  { label: "Activity", key: 1 },
  { label: "Nutrition", key: 2 },
  { label: "Recovery", key: 3 },
];

export const colorCodingFeedbackMessage: { [index: string]: string } = {
  1: "Excellent",
  2: "Good",
  3: "Neutral",
  4: "Warning",
  5: "Bad",
};

export const PNOE_WORKOUT_NAME: { [index: string]: string } = {
  1: "Cycling",
  2: "Running",
  3: "Hiking",
  4: "Swimming",
  5: "Rowing",
  6: "Martial Arts",
  7: "Play",
  8: "Pilates",
  9: "Yoga",
  10: "HIIT",
  11: "Cross Training",
  12: "Mixed Cardio",
  13: "Resistance Training",
  14: "Elliptical",
  15: "Functional Training",
  16: "Core Training",
  17: "Stair Climbing",
  18: "Jump Rope",
  19: "Boxing",
  20: "Dancing",
  21: "Housework",
  22: "Meditation",
  23: "Walking Fitness",
  24: "Weightlifting",
  25: "Mountain Biking",
  26: "Other Workout",
} as const;

export const getTimeValue = (num: number) => {
  const hours = Math.floor(num / 60) !== 0 ? Math.floor(num / 60) + " hr" : "";

  const min =
    num % 60 !== 0
      ? Math.round(num % 60) + " min"
      : hours !== ""
      ? ""
      : "0 min";

  return `${hours} ${min} `;
};

export const getCategoryColor = (
  unit: string,
  value: number = 0,
  theme: Theme,
  scoreCategory?: number
) => {
  if (unit === "%") {
    if (value < 20) {
      return theme.colorTokens.D[60];
    } else if (value < 40) {
      return theme.colorTokens.W[60];
    } else if (value < 60) {
      return theme.colorTokens.P[60];
    } else if (value < 80) {
      return theme.colorTokens.S[50];
    } else {
      return theme.colorTokens.S[60];
    }
  }

  switch (scoreCategory) {
    case 1:
      return theme.colorTokens.D[60];
    case 2:
      return theme.colorTokens.W[60];
    case 3:
      return theme.colorTokens.P[60];
    case 4:
      return theme.colorTokens.S[50];
    case 5:
      return theme.colorTokens.S[60];
  }
  return theme.colors_ds.NEUTRAL_TEXT_DEFAULT;
};

export const getBiomarkerCategoryColor = (
  type: number,
  theme: Theme,
  category: string
) => {
  if (category === "biomarkers") {
    switch (type) {
      case 1:
        return theme.colorTokens.W[70];
      case 2:
        return theme.colorTokens.P[50];
      case 3:
        return theme.colorTokens.S[60];
      case 4:
        return theme.colorTokens.D[60];
      default:
        return theme.colors_ds.NEUTRAL_TEXT_DEFAULT;
    }
  }
  if (category === "nutrition") {
    switch (type) {
      case 1:
        return theme.colorTokens.W[60];
      case 2:
        return theme.colorTokens.P[60];
      default:
        return theme.colorTokens.S[60];
    }
  }

  if (category === "trainingBreakdown") {
    switch (type) {
      case 1:
        return theme.colorTokens.D[50];
      case 2:
        return theme.colorTokens.W[40];
      default:
        return theme.colorTokens.P[60];
    }
  }
  return theme.colors_ds.NEUTRAL_BORDER_WEAK;
};

export const getNutritionProgrammingCategoryColor = (
  type: number,
  theme: Theme
) => {
  switch (type) {
    case 1:
      return theme.colorTokens.P[60];
    case 2:
      return theme.colorTokens.W[40];
    default:
      return theme.colorTokens.N[50];
  }
};

export enum ScoreCategory {
  SEVERE_LIMITATION = 1,
  LIMITATION = 2,
  NEUTRAL = 3,
  GOOD = 4,
  EXCELLENT = 5,
}

export const SCORE_CATEGORY_LABELS: { [key: number]: string } = {
  [ScoreCategory.SEVERE_LIMITATION]: "Severe limitation",
  [ScoreCategory.LIMITATION]: "Limitation",
  [ScoreCategory.NEUTRAL]: "Neutral",
  [ScoreCategory.GOOD]: "Good",
  [ScoreCategory.EXCELLENT]: "Excellent",
};

// Light-background / dark-text pairs for the category pill the side panel shows
// next to a value (the value itself is plain black — see PanelHeader/ScoreRow).
// `getCategoryColor` and `getBiomarkerCategoryColor` above give only the single
// accent color used for dots, rails and range bars; a pill needs both halves.
//
// The token weights match `getBiomarkerBadge` (healthOverview/biomarkerDisplay)
// on purpose: the health overview biomarker panel and the report-backed blood
// biomarker panel alternate for the same row, so their pills must be identical.
//
// `variant` picks the scale: "score" = ScoreCategory 1-5 (systems, metrics),
// "biomarkers" = BIOMARKER_CATEGORY_LABELS 1-4 (breath / blood biomarkers).
export type CategoryPillTokens = { background: string; color: string };

export const getCategoryPillTokens = (
  scoreCategory: number,
  theme: Theme,
  variant: "score" | "biomarkers" = "score"
): CategoryPillTokens => {
  const danger = { background: theme.colorTokens.D[10], color: theme.colorTokens.D[70] };
  const warning = { background: theme.colorTokens.W[20], color: theme.colorTokens.W[90] };
  const primary = { background: theme.colorTokens.P[10], color: theme.colorTokens.P[70] };
  const success = { background: theme.colorTokens.S[10], color: theme.colorTokens.S[80] };
  const neutral = {
    background: theme.colorTokens.N[30],
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  };

  if (variant === "biomarkers") {
    switch (scoreCategory) {
      case 1: // Borderline
        return warning;
      case 2: // Normal
        return primary;
      case 3: // Optimal
        return success;
      case 4: // Out of range
        return danger;
      default: // 0/None — no reference range, so no status
        return neutral;
    }
  }

  switch (scoreCategory) {
    case ScoreCategory.SEVERE_LIMITATION:
      return danger;
    case ScoreCategory.LIMITATION:
      return warning;
    case ScoreCategory.NEUTRAL:
      return primary;
    case ScoreCategory.GOOD:
    case ScoreCategory.EXCELLENT:
      return success;
    default:
      return neutral;
  }
};

export const getCategoryLabel = (
  unit: string,
  value: number,
  categories: {
    [key: string]: string;
  },
  scoreCategory?: number
) => {
  if (unit === "%") {
    if (value < 20) {
      return categories[1];
    } else if (value < 40) {
      return categories[2];
    } else if (value < 60) {
      return categories[3];
    } else if (value < 80) {
      return categories[4];
    } else {
      return categories[5];
    }
  }
  if (scoreCategory) {
    return categories[scoreCategory];
  }
  return "--";
};

export enum tabs {
  NONE = 0,
  CLIENTS = 1,
  DASHBOARD = 2,
  SCORE = 3,
  PROGRAMMING = 4,
  SERVICES = 5,
  CLIENT_INFO = 6,
}

export const personalInfoType = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phone",
  countryName: "countryName",
  countryID: "countryID",
  stateID: "stateID",
  stateName: "stateName",
  stateCode: "stateCode",
  countryCode: "countryCode",
  city: "city",
  address: "address",
  zip: "zip",
  report: "report",
  workout: "workout",
  nutrition: "nutrition",
};

export const biometricInfoType = {
  birth_date: "birth_date",
  gender: "gender",
  mass_measurement_unit: "mass_measurement_unit",
  length_measurement_unit: "length_measurement_unit",
  height: "height",
  current_weight: "current_weight",
  target_weight: "target_weight",
  fat_loss_intensity: "fat_loss_intensity",
  fat_mass_target: "fat_mass_target",
  muscle_mass_target: "muscle_mass_target",
  primary_fitness_goal: "primary_fitness_goal",
  secondary_fitness_goal: "secondary_fitness_goal",
  physical_activity: "physical_activity",
  occupation_type: "occupation_type",
  exercise_frequency: "exercise_frequency",
  cardio_training_level: "cardio_training_level",
  resistance_training_level: "resistance_training_level",
  interval_training_level: "interval_training_level",
  resistance_hypertrophy_focus_area: "resistance_hypertrophy_focus_area",
  resistance_strength_focus_area: "resistance_strength_focus_area",
  resistance_equipment: "resistance_equipment",
};

export const chartKeys = [
  // activity
  {
    key: "total-burn",
    label: "Total Burn",
    unit: "kcal",
    type: "act",
    showZeroValues: false,
  },
  {
    key: "general-activities",
    label: "General activities",
    unit: "kcal",
    type: "act",
    showZeroValues: false,
  },
  {
    key: "resting-metabolic-rate",
    label: "Resting Metabolic Rate",
    unit: "kcal",
    type: "act",
    showZeroValues: false,
  },
  {
    key: "running-cals",
    label: "Running",
    unit: "kcal",
    type: "act",
    showZeroValues: true,
  },
  {
    key: "resistance-cals",
    label: "Resistance training",
    unit: "kcal",
    type: "act",
    showZeroValues: true,
  },
  // nutrition
  {
    key: "cals",
    label: "Calorie intake",
    unit: "kcal",
    type: "nut",
    showZeroValues: false,
  },
  {
    key: "carbs",
    label: "Carbs",
    unit: "gr",
    type: "nut",
    showZeroValues: false,
  },
  {
    key: "protein",
    label: "Protein",
    unit: "gr",
    type: "nut",
    showZeroValues: false,
  },
  { key: "fat", label: "Fat", unit: "gr", type: "nut", showZeroValues: false },
  // micronutrients
  {
    key: "cholesterol_milligrams",
    label: "Cholesterol",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "sugars_grams",
    label: "Sugars",
    unit: "gr",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "saturated_fat_grams",
    label: "Saturated fat",
    unit: "gr",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "omega3_grams",
    label: "Omega-3",
    unit: "gr",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "iron_milligrams",
    label: "Iron",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "calcium_milligrams",
    label: "Calcium",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "iodine_micrograms",
    label: "Iodine",
    unit: "mcg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "sodium_milligrams",
    label: "Sodium",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "vitamin_d_micrograms",
    label: "Vitamin-D",
    unit: "mcg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "potassium_milligrams",
    label: "Potassium",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  {
    key: "magnesium_milligrams",
    label: "Magnesium",
    unit: "mg",
    type: "nut",
    showZeroValues: true,
  },
  // recovery
  {
    key: "sleep-duration",
    label: "Sleep Duration",
    unit: "hr",
    type: "rec",
    showZeroValues: false,
  },
  {
    key: "sleep-quality",
    label: "Sleep Quality",
    unit: "%",
    type: "rec",
    showZeroValues: false,
  },
  {
    key: "resting-heart-rate",
    label: "RHR",
    unit: "bpm",
    type: "rec",
    showZeroValues: false,
  },
  {
    key: "heart-rate-variability",
    label: "HRV daily",
    unit: "bpm",
    type: "rec",
    showZeroValues: false,
  },
  {
    key: "sleep-heart-rate-variability",
    label: "HRV sleep",
    unit: "bpm",
    type: "rec",
    showZeroValues: false,
  },
];

export const getBarColour = (
  index: number,
  length: number,
  type: string,
  theme: any
) => {
  if (length === 2) {
    switch (index) {
      case 0:
        return theme.colors_ds.PRIMARY_GRAPH_MAIN;
      default:
        return theme.colors_ds.SUCCESS_ICON_MAIN;
    }
  }
  if (length === 3) {
    if (type === "withPercentageAndTime") {
      switch (index) {
        case 0:
          return theme.colorTokens.P[80];
        case 1:
          return theme.colorTokens.P[50];
        case 2:
          return theme.colorTokens.P[20];
        default:
          return theme.colors_ds.NEUTRAL_BORDER_WEAK;
      }
    } else {
      switch (index) {
        case 0:
          return theme.colorTokens.S[60];
        case 1:
          return theme.colorTokens.W[60];
        case 2:
          return theme.colorTokens.P[60];
        default:
          return theme.colors_ds.NEUTRAL_BORDER_WEAK;
      }
    }
  }
  if (length === 4) {
    switch (index) {
      case 0:
        return theme.colorTokens.S[60];
      case 1:
        return theme.colorTokens.D[60];
      case 2:
        return theme.colorTokens.W[60];
      case 3:
        return theme.colorTokens.P[60];
      default:
        return theme.colors_ds.NEUTRAL_BORDER_WEAK;
    }
  }

  if (length === 5) {
    switch (index) {
      case 0:
        return theme.colorTokens.P[30];
      case 1:
        return theme.colorTokens.P[70];
      case 2:
        return theme.colorTokens.S[70];
      case 3:
        return theme.colorTokens.W[70];
      case 4:
        return theme.colorTokens.D[70];
      default:
        return theme.colors_ds.NEUTRAL_BORDER_WEAK;
    }
  }
};

export const getZoneRange = (item: any, zone: number) => {
  if (zone === 1) {
    return `<${item.max_value} BPM`;
  }
  if (zone === 5) {
    return `${item.min_value}+ BPM`;
  }
  return `${item.min_value} - ${item.max_value} BPM`;
};

export const pad = (d: number) => {
  return d < 10 ? "0" + d.toString() : d.toString();
};

export const getShortTimeValue = (num: number) => {
  const hours = pad(Math.floor(num / 60));
  const min = pad(Math.round(num % 60));

  return `${hours}:${min}`;
};

export const isHighLowMetric = (id: string): boolean =>
  id === "8_RMR" || id === "8_AMR";

export const getHighLowLabel = (score: number): string =>
  score === 100 ? "High" : "Low";

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const PointerChartType = {
  reverseValues: 1,
  initialValues: 2,
  HR_zonesTab: 3,
  recovery: 4,
};

// Mirrors the backend `enPrecisionKind` enum (source of truth). Labels use the
// enum's [Display(Name)] where present, otherwise the member name (Nails,
// Biopsies). Keep in sync when the backend enum changes.
export const enPrecisionTestKind: Record<number, string> = {
  0: "None",
  1: "RMR",
  2: "AMR",
  3: "Whole Blood",
  4: "Body composition",
  5: "Saliva",
  6: "Stool",
  7: "Urine",
  8: "Indexes",
  9: "Hair",
  10: "Light",
  11: "Vital signs",
  12: "Nails",
  13: "Serum/plasma",
  14: "Cerebrospinal fluid",
  15: "Biopsies",
  16: "Cell cultures",
  17: "Nasopharyngeal throat swabs",
  18: "Prenatal samples",
  19: "Synovial Fluid",
  20: "Peritoneal Fluid",
  21: "Pancreatic Fluid",
  22: "Pleural Fluid",
  23: "Pancreatic Cyst Fluid",
  24: "Bone marrow",
  25: "Pericardial Fluid",
};

// A panel's items can each carry a different test kind, so a single panel-level
// kind isn't representative. Collect the distinct kinds across the panel's items
// (falling back to the panel-level `kind` only when it has no items).
export const getPanelTestKinds = (panel: any): number[] => {
  const items = Array.isArray(panel?.items) ? panel.items : [];
  const kinds: number[] = items
    .map((item: any) => item?.kind)
    .filter((k: any) => k !== null && k !== undefined);
  if (
    kinds.length === 0 &&
    panel?.kind !== null &&
    panel?.kind !== undefined
  ) {
    kinds.push(panel.kind);
  }
  // Distinct, preserving first-seen order.
  return Array.from(new Set<number>(kinds));
};

// Comma-joined labels of a panel's distinct test kinds, e.g. "Serum/plasma, Urine".
export const getPanelTestKindsLabel = (panel: any): string =>
  getPanelTestKinds(panel)
    .map((kind) => enPrecisionTestKind[kind] || "")
    .filter(Boolean)
    .join(", ");

// Display a comparator symbol (">=" → "≥", "<=" → "≤"); pass others through.
const displayRangeOp = (op: string): string =>
  op === ">=" ? "≥" : op === "<=" ? "≤" : op || "";

// The core (unit-less) type-2 range value, built from the canonical bounds:
//   both bounds  → "min–max"      (two-sided inclusive range, e.g. "3–4")
//   max only     → "<max"/"≤max"  (e.g. "<2")
//   min only     → ">min"/"≥min"  (e.g. "≥5")
// Falls back to `qvalue` when no numeric bound is present.
const buildRangeValue = (item: any): string => {
  const hasMin = item.rangeMinValue !== null && item.rangeMinValue !== undefined;
  const hasMax = item.rangeMaxValue !== null && item.rangeMaxValue !== undefined;
  if (hasMin && hasMax) return `${item.rangeMinValue}–${item.rangeMaxValue}`;
  if (hasMax) return `${displayRangeOp(item.rangeMaxOp)}${item.rangeMaxValue}`;
  if (hasMin) return `${displayRangeOp(item.rangeMinOp)}${item.rangeMinValue}`;
  return item.qvalue ? String(item.qvalue) : "";
};

// A lab-result item's display value, driven by its `type`:
//  1 quantitative      → numeric value + unit           ("50 x10³/µL")
//  2 range / threshold → operator + bound(s) + unit      ("<2 m", "3–4 ml", "≥5 kg")
//  3 qualitative       → qualitative string as-is        ("NEGATIVE", "B")
//  0 / anything else   → em dash (no result yet)
export const formatBiomarkerResultValue = (item: any): string => {
  if (!item) return "—";
  const unit = item.unit ? ` ${item.unit}` : "";
  switch (item.type) {
    case 1:
      return item.value !== null && item.value !== undefined
        ? `${item.value}${unit}`
        : "—";
    case 2: {
      const core = buildRangeValue(item);
      return core ? `${core}${unit}` : "—";
    }
    case 3:
      return item.qvalue !== null &&
        item.qvalue !== undefined &&
        item.qvalue !== ""
        ? String(item.qvalue)
        : "—";
    default:
      return "—";
  }
};

// The biomarker TAB / dashboard payload is snake_case with a `qualitative`
// flag (distinct from the panel-results camelCase `type` shape above):
//   qualitative === true          → the qvalue string ("AB+"), authored via qoptions
//   range value (Junction import) → operator + bound(s) ("<2.0", "≥3 ≤4"), render-only
//   quantitative                  → numeric value + unit ("50 x10³/µL")
// `v` is a single value entry (or the biomarker itself); it carries
// value/qvalue/range_min_*/range_max_*. Range values are created only through
// Junction, so the UI renders but never authors them.
export const isRangeBiomarkerValue = (v: any): boolean =>
  !!v &&
  ((typeof v.range_min_op === "string" && v.range_min_op !== "") ||
    (typeof v.range_max_op === "string" && v.range_max_op !== ""));

export const formatBiomarkerTabValue = (
  v: any,
  unit: string,
  qualitative?: boolean
): string => {
  const suffix = unit ? ` ${unit}` : "";
  if (!v) return "---";
  // Qualitative: string qvalue, no unit.
  if (qualitative) {
    return v.qvalue != null && v.qvalue !== "" ? String(v.qvalue) : "---";
  }
  // Range (import-only): each operator sits on its own bound — "<"/"<=" upper,
  // ">"/">=" lower. Render whichever bound(s) carry an operator, else fall back
  // to the pre-formatted qvalue string.
  if (isRangeBiomarkerValue(v)) {
    const parts: string[] = [];
    if (v.range_min_op)
      parts.push(`${displayRangeOp(v.range_min_op)}${v.range_min_value}`);
    if (v.range_max_op)
      parts.push(`${displayRangeOp(v.range_max_op)}${v.range_max_value}`);
    let core = parts.join(" ");
    if (!core && v.qvalue) core = String(v.qvalue);
    return core ? `${core}${suffix}` : "---";
  }
  // Quantitative.
  return `${v.value}${suffix}`;
};

// Detect a range-style OCR/extractor value string: a leading comparator
// (< > <= >= ≤ ≥) or a two-sided "a-b"/"a–b" numeric range. A plain number is
// NOT a range. Used by the extractor to lock non-editable range values.
export const isRangeString = (v: string): boolean => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (s === "") return false;
  if (/^(<=|>=|≤|≥|<|>)\s*-?\d/.test(s)) return true; // "<2.0", "≥ 5"
  if (/^-?\d+(\.\d+)?\s*[-–]\s*-?\d+(\.\d+)?$/.test(s)) return true; // "3-4"
  return false;
};

// True when a string is a plain finite number ("2", "3.4", "6980.00"). Non-
// numeric text like "Nil"/"Positive" and empty strings are false. Used by the
// extractor to route non-numeric values (that aren't qualitative or range) to
// the "couldn't convert" bucket instead of a broken numeric input.
export const isNumericString = (v: any): boolean => {
  const s = String(v ?? "").trim();
  if (s === "") return false;
  const n = Number(s);
  return !isNaN(n) && isFinite(n);
};

// Strip insignificant trailing zeros from each numeric bound of a range string,
// preserving the operator/separator: "90.00-120.00" → "90-120", "<0.50" → "<0.5",
// "150000-410000" → unchanged. Non-range strings are returned as-is.
export const formatRangeString = (v: string): string => {
  const s = String(v ?? "").trim();
  if (s === "") return s;
  const single = s.match(/^(<=|>=|≤|≥|<|>)\s*(-?\d+(?:\.\d+)?)$/);
  if (single) return `${single[1]}${String(Number(single[2]))}`;
  const two = s.match(/^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)$/);
  if (two) return `${String(Number(two[1]))}-${String(Number(two[2]))}`;
  return s;
};

// Parse a range string into canonical bounds, reassigning each operator to its
// correct bound: "<"/"<=" are upper bounds, ">"/">=" are lower bounds; a bare
// "a-b" fills both. Mirrors the operator→bound convention in labResults.ts.
export const parseRangeString = (
  v: string
): {
  range_min_op: string;
  range_min_value: number;
  range_max_op: string;
  range_max_value: number;
} => {
  const empty = {
    range_min_op: "",
    range_min_value: 0,
    range_max_op: "",
    range_max_value: 0,
  };
  if (typeof v !== "string") return empty;
  const s = v.trim();

  const single = s.match(/^(<=|>=|≤|≥|<|>)\s*(-?\d+(?:\.\d+)?)$/);
  if (single) {
    const op = single[1] === "≤" ? "<=" : single[1] === "≥" ? ">=" : single[1];
    const num = parseFloat(single[2]);
    if (op === "<" || op === "<=") {
      return { ...empty, range_max_op: op, range_max_value: num };
    }
    return { ...empty, range_min_op: op, range_min_value: num };
  }

  const two = s.match(/^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)$/);
  if (two) {
    return {
      range_min_op: "",
      range_min_value: parseFloat(two[1]),
      range_max_op: "",
      range_max_value: parseFloat(two[2]),
    };
  }
  return empty;
};

export const enBiomarkerCategory: Record<number, string> = {
  0: "Other",
  1: "Vitamins",
  2: "Proteins",
  3: "Enzymes",
  4: "Lipids",
  5: "Hormones",
  6: "Minerals",
  7: "Amino Acids",
  8: "Carbohydrate structures",
  9: "Metabolites",
  10: "Cells",
  11: "Heavy Metals",
  12: "Drugs",
  13: "Substances",
  14: "Pathogens",
  15: "Antigens",
  16: "Antibodies",
  17: "Auto-antibodies",
  18: "Cytokines",
  19: "Genomic Alterations",
  20: "Circulating Genomic Markers",
  21: "Reactive Oxygen Species (ROS) byproducts",
  22: "Toxins",
  23: "Body Composition Markers",
  24: "Peptides",
};

export const userStatus = {
  None: 0,
  Pending: 1,
  InProgress: 2,
  Completed: 3,
};
